"use client";

/**
 * EmailSendButton
 * Public landing page CTA for an email link (`#__email-{templateId}`). Clicking sends the button's
 * email to the signed-in viewer. SmartButton renders this only for email links that carry a button
 * id: the send flow needs the toast provider the public page mounts, and SmartButton's other callers
 * (pricing, card grid, venue, renderer tests) have none.
 */

import { useCallback } from "react";
import type React from "react";
import {
  useEmailButtonSend,
  type EmailButtonSendOutcome,
  type EmailButtonSendStatus,
  type EmailButtonSendTarget,
} from "../../_hooks/useEmailButtonSend";
import { useSyntheticClickGuard } from "../../_hooks/useSyntheticClickGuard";

/** Screen reader announcement for each send status. Idle announces nothing. */
const STATUS_ANNOUNCEMENTS: Record<EmailButtonSendStatus, string> = {
  idle: "",
  sending: "Sending email",
  sent: "Email sent",
  redirecting: "Redirecting to sign in",
};

interface EmailSendButtonProps {
  /** Landing page, event, and button the send is for */
  sendTarget: EmailButtonSendTarget;
  /** Button label shown while idle */
  label: string;
  /** Classes shared with SmartButton's other CTAs */
  className: string;
  /** Inline styles shared with SmartButton's other CTAs, including the background color */
  style: React.CSSProperties;
  /** Analytics callback, called once per completed send */
  onOutcome?: (outcome: EmailButtonSendOutcome) => void;
}

/**
 * Picks the visible button label for a send status.
 * @param status - Current send status
 * @param label - The button's own label
 * @returns "Sending..." while sending, "Sent" after success, otherwise the button's label
 */
function getButtonLabel(status: EmailButtonSendStatus, label: string): string {
  if (status === "sending") return "Sending...";
  if (status === "sent") return "Sent";
  return label;
}

/**
 * Renders the email send button and a visually hidden status region that announces its progress.
 * @param props - Send target, label, shared CTA styling, and the analytics callback
 * @returns The button followed by its status region
 * @throws If rendered outside a FetchToastProvider
 */
export function EmailSendButton({
  sendTarget,
  label,
  className,
  style,
  onOutcome,
}: EmailSendButtonProps): React.ReactElement {
  // Hooks
  const { status, send } = useEmailButtonSend(sendTarget, onOutcome);
  const { shouldIgnoreSyntheticClick, markTouchActivation } = useSyntheticClickGuard();

  // Derived values
  const isIdle = status === "idle";
  const buttonClassName = isIdle ? className : `${className} cursor-default`;

  // Handlers
  const handleClick = useCallback(() => {
    // A tap already sent from touchend; skip the click the browser synthesizes after it.
    if (shouldIgnoreSyntheticClick()) return;
    void send();
  }, [send, shouldIgnoreSyntheticClick]);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      markTouchActivation();
      e.preventDefault();
      void send();
    },
    [markTouchActivation, send]
  );

  // Render
  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        disabled={!isIdle}
        aria-busy={status === "sending"}
        className={buttonClassName}
        style={style}
      >
        {getButtonLabel(status, label)}
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        {STATUS_ANNOUNCEMENTS[status]}
      </span>
    </>
  );
}
